import React, { useState, useEffect } from "react";
import { Heart } from "lucide-react";
import { getImageVariants } from "../utils/imageUtils";
import { PlaceItem } from "../types/explore";
import { useSavedPlaces } from "../contexts/SavedPlacesContext";
import { usePermissions } from "../hooks/usePermissions";
import RegistrationPrompt from "./RegistrationPrompt";

type ImageVariants = {
  tiny: string | null;
  thumb: string | null;
  medium: string | null;
  large: string | null;
  original: string | null;
};

type ExploreCardProps = {
  place: PlaceItem;
  onClick: (place: PlaceItem) => void;
  onDoubleClick?: (place: PlaceItem) => void;
  onHover?: (place: PlaceItem | null) => void;
  onTouch?: (place: PlaceItem) => void;
  className?: string;
  showOverlay?: boolean;
  onOverlayClick?: (place: PlaceItem) => void;
};

const ExploreCard: React.FC<ExploreCardProps> = ({ place, onClick, onDoubleClick, onHover, onTouch, className, showOverlay, onOverlayClick }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);
  const { isPlaceSaved, savePlace, unsavePlace } = useSavedPlaces();
  const { canSave } = usePermissions();
  const isSaved = isPlaceSaved(place.id);
  const fallbackImage = "https://via.placeholder.com/150?text=No+Image";

  const getImageSrc = (photo: string): string => {
    const variants = getImageVariants(photo) as ImageVariants;
    return variants.thumb || variants.original || fallbackImage;
  };

  const generateSrcSet = (photo: string): string | undefined => {
    const variants = getImageVariants(photo) as ImageVariants;
    if (!variants.tiny) return undefined;
    
    return [
      `${variants.tiny} 150w`,
      `${variants.thumb} 300w`,
      `${variants.medium} 600w`,
      `${variants.large} 1200w`
    ].join(', ');
  };

  const handleClick = (e: React.MouseEvent) => {
    // Always trigger single-click action immediately for responsiveness
    onClick(place);
    
    // Check for double-click
    if (clickTimeout) {
      // This is a double-click - clear timeout and trigger double-click action
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      if (onDoubleClick) {
        onDoubleClick(place);
      }
    } else {
      // First click - set timeout to detect double-click
      const timeout = setTimeout(() => {
        setClickTimeout(null);
      }, 300);
      setClickTimeout(timeout);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter key opens the panel (for keyboard users)
    if (e.key === 'Enter' && onDoubleClick) {
      e.preventDefault();
      onDoubleClick(place);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    e.preventDefault(); // Prevent any default behavior
    
    // Check permissions before saving (unsaving is always allowed)
    if (!isSaved && !canSave().allowed) {
      setShowRegistrationPrompt(true);
      return;
    }
    
    // Start animation
    setIsAnimating(true);
    
    try {
      if (isSaved) {
        await unsavePlace(place.id);
      } else {
        await savePlace(place);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      // Stop animation after a short delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 600);
    }
  };

  const handleSaveTouch = async (e: React.TouchEvent) => {
    e.stopPropagation(); // Prevent triggering the card touch
    
    // Check permissions before saving (unsaving is always allowed)
    if (!isSaved && !canSave().allowed) {
      setShowRegistrationPrompt(true);
      return;
    }
    
    // Start animation
    setIsAnimating(true);
    
    try {
      if (isSaved) {
        await unsavePlace(place.id);
      } else {
        await savePlace(place);
      }
    } catch (error) {
      // Silently handle error
    } finally {
      // Stop animation after a short delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 600);
    }
  };

  const handleMouseEnter = () => {
    onHover?.(place);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // Record the initial touch position
    const touch = e.touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Calculate distance moved
    if (!touchStart) return;
    
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStart.x);
    const deltaY = Math.abs(touch.clientY - touchStart.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    // Only trigger tap if movement was less than 10px (indicating a tap, not a scroll)
    if (distance < 10) {
      onTouch?.(place);
    }
    
    // Reset touch start position
    setTouchStart(null);
  };

  return (
    <div
      className={`cursor-pointer transition-opacity duration-200 hover:opacity-95 ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      tabIndex={0}
      role="button"
      aria-label={`${place.title} - ${place.type} in ${place.city || 'Sri Lanka'}`}
    >
      {/* Image */}
      <div className="aspect-square relative overflow-hidden rounded-t-2xl mb-2">
        <img
          src={place.photo_1_cdn ? getImageSrc(place.photo_1_cdn) : fallbackImage}
          srcSet={place.photo_1_cdn ? generateSrcSet(place.photo_1_cdn) : undefined}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={place.title}
          className="w-full h-full object-cover rounded-b-2xl"
          loading="lazy"
          decoding="async"
        />
        {/* Type Badge */}
        <div className="absolute top-2 left-2">
          <span className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full capitalize">
            {place.type}
          </span>
        </div>
        {/* Rating Badge */}
        {place.rating && (
          <div className="absolute top-2 right-2">
            <span className="bg-white text-gray-900 text-xs font-semibold px-2.5 py-1.5 rounded-xl flex items-center gap-1 shadow-md backdrop-blur-sm bg-opacity-95">
              ⭐ {typeof place.rating === 'number' ? place.rating.toFixed(1) : place.rating}
            </span>
          </div>
        )}

        {/* Save Heart Icon */}
        <button
          onClick={handleSaveClick}
          onTouchStart={handleSaveTouch}
          className={`heart-icon absolute bottom-2 right-2 p-1.5 rounded-full transition-all duration-300 z-20 ${
            isSaved 
              ? 'bg-red-500 text-white shadow-lg' 
              : 'bg-white bg-opacity-80 text-gray-600 hover:bg-opacity-100 hover:text-red-500'
          } ${isAnimating ? 'animate-pulse scale-110' : ''}`}
        >
          <Heart 
            size={16} 
            fill={isSaved ? 'currentColor' : 'none'}
            className={`transition-all duration-300 ${
              isAnimating ? 'scale-125' : ''
            }`}
          />
        </button>

        {/* Touch Overlay for Mobile - Only on Image */}
        {showOverlay && onOverlayClick && (
          <div
            className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center cursor-pointer z-10 rounded-lg"
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click
              // Don't trigger overlay if heart icon was clicked
              if (!(e.target as HTMLElement).closest('.heart-icon')) {
                onOverlayClick(place);
              }
            }}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent card touch handlers
            }}
            onTouchEnd={(e) => {
              e.stopPropagation(); // Prevent card touch
              e.preventDefault(); // Prevent click event from firing
              // Don't trigger overlay if heart icon was clicked
              if (!(e.target as HTMLElement).closest('.heart-icon')) {
                onOverlayClick(place);
              }
            }}
          >
            <div className="bg-white bg-opacity-90 px-4 py-2 rounded-lg shadow-lg">
              <span className="text-gray-800 font-medium text-sm">Show More</span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-0.5">
        <h3 className="font-medium text-sm text-gray-900 line-clamp-2">
          {place.title}
        </h3>
        
        {place.city && (
          <p className="text-xs text-gray-500">
            {place.city}
          </p>
        )}

        {/* Price Range */}
        {(place.price_per_night_dec || place.cost_per_person_min) && (
          <p className="text-xs text-gray-600">
            {place.price_per_night_dec ? (
              <span>Rs. {place.price_per_night_dec} / night</span>
            ) : place.cost_per_person_min ? (
              <span>Rs. {place.cost_per_person_min}-Rs. {place.cost_per_person_max || '∞'} / person</span>
            ) : null}
          </p>
        )}
      </div>

      {/* Registration Prompt */}
      <RegistrationPrompt
        isOpen={showRegistrationPrompt}
        onClose={() => setShowRegistrationPrompt(false)}
        variant="google_auth"
        trigger="save"
      />
    </div>
  );
};

export default ExploreCard;
